import React, { Fragment, useCallback, useState } from 'react';
import _get from 'lodash/get';
import { getPaxDeliveryError } from 'common/anytime-booking/delivery';
import cx from 'classnames';
import I18n from 'i18n-js';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import NewBookingDuck from 'ducks/new-booking-duck';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import { ROUTE_OPTIMIZE_STATUS_VALUE } from 'applications/scribe/instrumentations/track';
import DragHandler from 'public/icons/drag-handle-dots.svg';
import reorder from 'helper/reorder';
import { truncateText } from 'helper/anytime-booking';
import separateSvg from 'public/icons/bulkupload/separeate.svg';
import CommonDragAndDrop from '../common/drag-and-drop';
import NumberedPin from '../../common/numbered-pin';
import styles from './expanded-delivery.less';
import RouteOptimizationButton from '../common/route-optimization-button';
import ConfirmModal from '../../confirm-modal';

export const ExpandedDeliveryView = ({ delivery }) => {
  const dispatch = useDispatch();
  const fetchingQuote = _get(delivery, 'fetchingQuote');
  const disabled = !!getPaxDeliveryError(delivery);
  const dropOffs = _get(delivery, ['detail', 'dropOffs']);
  const deliveryID = _get(delivery, 'id');
  const [optimizing, setOptimizing] = useState(false);
  const [
    confirmSeparateModalVisible,
    setConfirmSeparateModalVisible,
  ] = useState(false);
  const [swapOutIndex, setSwapOutIndex] = useState(undefined);

  const onSeparateClick = useCallback(
    index => () => {
      if (index !== undefined) {
        setSwapOutIndex(index);
        setConfirmSeparateModalVisible(true);
      }
    },
    [setSwapOutIndex, setConfirmSeparateModalVisible],
  );

  const onSeparateDropOff = useCallback(
    removeIndex => () => {
      dispatch(
        NewBulkUploadDuck.creators.splitDropOff(deliveryID, removeIndex),
      );

      setConfirmSeparateModalVisible(false);
    },
    [dispatch, deliveryID, dropOffs, setConfirmSeparateModalVisible],
  );

  const dropOffsRenderFunc = useCallback(
    (dropOff, index) => (
      <div
        // eslint-disable-next-line
        key={`${index}-${_get(dropOff, 'poi.id')}`}
        className={cx(styles.deliveryStep, {
          [styles.disabledDelivery]: disabled,
        })}
      >
        <span className={styles.placeholder}>
          <Fragment>
            <div className={styles.deleteIcon} onClick={onSeparateClick(index)}>
              <img src={separateSvg} alt="" />
            </div>
            <img className={styles.draggable} src={DragHandler} alt="" />
          </Fragment>
        </span>
        <span className={styles.nameWithPin}>
          <NumberedPin
            showNumber
            number={`${index + 1}`}
            className={styles.pin}
          />
          {truncateText(_get(dropOff, 'name'))}
        </span>
        <div className={styles.address}>
          {_get(dropOff, 'poi.address.combined_address')}
        </div>
        <div className={styles.weight}>{_get(dropOff, 'itemWeight')}kg</div>
      </div>
    ),
    [onSeparateDropOff],
  );

  const onEnableRouteOptimization = useCallback(
    () => {
      setOptimizing(true);
      setTimeout(() => setOptimizing(false), 1000);
      const optimizedSteps = _get(
        delivery,
        'quote.alternativeFare[0].routeOptimization',
      );
      dispatch(
        NewBookingDuck.creators.applyRouteOptimization(
          deliveryID,
          optimizedSteps,
        ),
      );
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBulkUploadSetupDeliveryFindShortestRoute(
          deliveryID,
        ),
      );
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBulkUploadSetupDeliveryFindShortestRouteResults(
          deliveryID,
          ROUTE_OPTIMIZE_STATUS_VALUE.OPTIMIZED_SUCCESS,
        ),
      );
    },
    [dispatch, delivery, deliveryID, setOptimizing],
  );
  const onRevertRouteOptimization = useCallback(
    () => {
      dispatch(NewBookingDuck.creators.revertRouteOptimization(deliveryID));
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBulkUploadSetupDeliveryUseOriginalRoute(
          deliveryID,
        ),
      );
    },
    [dispatch, deliveryID],
  );

  const onDragEnd = useCallback(
    result => {
      const sourceIndex = _get(result, 'source.index');
      const destIndex = _get(result, 'destination.index');
      if (sourceIndex === undefined || destIndex === undefined) {
        return;
      }
      const newDropOffs = reorder(dropOffs, sourceIndex, destIndex);
      dispatch(NewBookingDuck.creators.setDropOffs(deliveryID, newDropOffs));
    },
    [dispatch, deliveryID, dropOffs],
  );

  return (
    <div className={cx(styles.expandedDelivery)}>
      <Fragment>
        <ConfirmModal
          visible={confirmSeparateModalVisible}
          title={'Separate this recipient from multi-stop delivery?'}
          body={'The will become a delivery on its own.'}
          okText={'Separate It'}
          cancelText={'No'}
          onOk={onSeparateDropOff(swapOutIndex)}
          onCancel={() => setConfirmSeparateModalVisible(false)}
        />
        <CommonDragAndDrop
          rawData={dropOffs}
          onDragEndFunc={onDragEnd}
          customStyles={styles.draggableDropOffs}
          renderFunc={dropOffsRenderFunc}
        />
      </Fragment>

      {dropOffs.length > 1 && (
        <Fragment>
          <div className={styles.roButton}>
            <RouteOptimizationButton
              delivery={delivery}
              onEnableRouteOptimization={onEnableRouteOptimization}
              onRevertRouteOptimization={onRevertRouteOptimization}
            />
          </div>
          <div
            className={cx(styles.loadingOverlay, {
              [styles.hidden]: !fetchingQuote,
            })}
          >
            <div className={styles.fetching} />
            <p>
              {optimizing
                ? I18n.t('v2.locationDrawer.overlayTitle')
                : I18n.t('v2.locationDrawer.updatingRoute')}
            </p>
          </div>
        </Fragment>
      )}
    </div>
  );
};

ExpandedDeliveryView.propTypes = {
  delivery: PropTypes.shape({}).isRequired,
};
const ExpandedDeliveryRender = delivery => {
  if (_get(delivery, ['detail', 'dropOffs', 'length'], 0) < 2) {
    return undefined;
  }
  return <ExpandedDeliveryView delivery={delivery} />;
};

export default ExpandedDeliveryRender;
